import React, { useState, useEffect, useMemo } from 'react';
import { useWeb } from '../../../context/Dashboard/contextWeb';
import { useReports } from '../../../context/Dashboard/contextReports';
import { useChat } from '../../../context/contextChat';
import { Link } from 'react-router-dom';
import CustomModal from '../../modal';
import CveEpssTable, { consolidateCveEntries } from './CveEpssTable';
import './style.css';

const calculateScore = (risk, confidence) => {
  if (!risk) return 0.0;
  risk = String(risk).toLowerCase();
  if (risk === "informational" || risk === "info") return 0.0;

  const riskMap = {
    low: 3.0,
    medium: 6.0,
    high: 8.0,
    critical: 9.5
  };

  const confidenceMap = {
    low: -0.3,
    medium: 0,
    high: 0.3
  };

  const r = riskMap[risk] ?? 0;
  const c = confidenceMap[String(confidence).toLowerCase()] ?? 0;

  let score = r + c;

  return Math.max(0, Math.min(10, Number(score.toFixed(1))));
};

const calculateBackgroundColor = (riskFactor) => {
  switch (riskFactor) {
    case 'Critical': return '#6d2d9d';
    case 'High': return '#fc0000';
    case 'Medium': return '#ffc000';
    case 'Low': return '#92d14f';
    case 'Informalional': return '#006dc0';
    default: return '#006dc0';
  }
};

const getSeverityString = (level) => {
  if (level === 'Critical') return "Critical";
  if (level === 'High') return "High";
  if (level === 'Medium') return "Medium";
  if (level === 'Low') return "Low";
  if (level === 'Informational') return "Info";
  return "None";
};

const RiskExposureList = ({
  bHeightVal,
  urlVulTable,
  urlEpssTable = [],
  scanIncomplete = false
}) => {
  // bHeightVal is 80vh. Subtracting another 80vh collapsed the result card
  // to zero height; only the footer button remained visible. Reserve a small
  // amount for the scanned-URL header instead.
  const tableHeight = `calc(${bHeightVal || '80vh'} - 80px)`;
  const props = { bHeightVal, urlVulTable, urlEpssTable };
  
  return (
    <div className="p-2 risk-exposure-list">
      <div className="card rounded-4 shadow-sm h-100 overflow-hidden risk-exposure-card"
        style={{ backgroundColor: '#f9f9f9' }}>
        <div className="card-body d-flex flex-column" style={{ height: tableHeight }}>
          {!Array.isArray(urlVulTable) || urlVulTable.length === 0 ? (
            <div className="h-100 d-flex flex-column justify-content-center align-items-center text-center">
              {scanIncomplete ? (
                <>
                  <i className="bi bi-exclamation-triangle-fill text-warning display-1 mb-3" />
                  <h2 className="text-dark">Scan completed with warnings</h2>
                  <p className="text-secondary mb-0 px-4">
                    The available results are partial and must not be treated as a clean scan.
                  </p>
                </>
              ) : (
                <>
                  <div className="icon-loader">
                    <i className="bi bi-shield-fill-check" />
                  </div>
                  <h1>
                    No <span style={{ color: '#e00f0f' }}>Vulnerability</span> Detected
                  </h1>
                </>
              )}
            </div>
          ) : (
            <>
              <TableHeader />
              <div className="scrollbar-all-primary">
                <ExposureTable {...props} />
              </div>
              <BtnArea />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RiskExposureList;

const TableHeader = () => (
  <>
    <div className="h-4 card-title text-center mb-3 pt-3 fw-bold text-dark">Risk Exposure List</div>
    <div className="col-12 mb-3 d-flex px-3" style={{ fontSize: 16, fontWeight: 600 }}>
      <div className='text-center' style={{ width: '60%', marginRight: 4 }}>Vulnerability</div>
      <div className='text-center' style={{ width: '20%', marginRight: 4 }}>Severity</div>
      <div className='text-center' style={{ width: '20%' }}>CVSS</div>
    </div>
  </>
);

const ExposureTable = ({ urlVulTable, urlEpssTable }) => {
  const { openVulnerabilityActions } = useChat();
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [activeModalTab, setActiveModalTab] = useState('details');
  const cveCount = useMemo(
    () => consolidateCveEntries(urlEpssTable).length,
    [urlEpssTable]
  );

  useEffect(() => {
    if (urlVulTable) {
      setVulnerabilities(urlVulTable);
    }
  }, [urlVulTable]);

  const showFindingDetails = (finding) => {
    setActiveModalTab('details');
    setSelectedFinding(finding);
  };

  const closeFindingDetails = () => {
    setSelectedFinding(null);
    setActiveModalTab('details');
  };

  const handleCveClick = (cve) => {
    closeFindingDetails();
    openVulnerabilityActions({
      contextType: 'cve',
      cveId: cve.cveId,
      epss: cve.epss,
      severity: cve.baseSeverity,
      exploitability: cve.exploitability,
      impact: cve.impact,
      confidentiality: cve.confidentiality,
      integrity: cve.integrity,
      availability: cve.availability,
      baseScore: cve.baseScore,
      remediation: cve.remediation,
    });
  };

  const handleFindingAnalysis = () => {
    if (!selectedFinding) return;

    const findingContext = {
      contextType: 'finding',
      alert: selectedFinding.alert,
      risk: selectedFinding.risk,
      confidence: selectedFinding.confidence,
      url: selectedFinding.url,
      description: selectedFinding.description,
      evidence: selectedFinding.evidence,
      method: selectedFinding.method,
      param: selectedFinding.param,
      cweid: selectedFinding.cweid,
      wascid: selectedFinding.wascid,
      pluginId: selectedFinding.pluginId,
      source: selectedFinding.source,
      detectedBy: selectedFinding.detectedBy,
      solution: selectedFinding.solution,
    };

    closeFindingDetails();
    openVulnerabilityActions(findingContext);
  };

  const displayValue = (value, fallback = 'Not provided') => {
    if (Array.isArray(value)) return value.filter(Boolean).join('\n') || fallback;
    if (value && typeof value === 'object') return JSON.stringify(value, null, 2);
    return value ?? fallback;
  };

  return (
    <div className="px-4">
      {vulnerabilities.map((vuln, index) => {
        return (
          <div
            className="py-2 d-flex flex-row align-items-center border-bottom"
            key={vuln?.fingerprint || `${vuln?.cveId || vuln?.alert || 'finding'}-${index}`}
            style={{ fontSize: 14 }}>
          <div
            style={{
              width: 300,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 600,
              color: '#343434',
            }}>
            <span className="fw-semibold text-dark">
              {vuln.alert}
            </span>
          </div>

          <div className="text-center">
            <span
              className="badge rounded-pill fw-bold fs-6 px-3 py-1 my-1"
              style={{
                background: calculateBackgroundColor(vuln.risk),
                color: '#fff',
                width: 100,
                cursor: 'pointer',
              }}
              onClick={() => showFindingDetails(vuln)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  showFindingDetails(vuln);
                }
              }}
              title="View vulnerability details"
            >
              {getSeverityString(vuln.risk)}
            </span>
          </div>

          <div className="text-start">
            <span className="badge badge-cvss bg-secondary fw-bold ms-3" style={{ fontSize: 13 }}>
              {calculateScore(vuln.risk, vuln.confidence)}
            </span>
          </div>
          </div>
        );
      })}

      <div className="text-center py-3 mb-5">
        <div style={{ width: 220, height: 10 }} />
      </div>

      <CustomModal
        show={Boolean(selectedFinding)}
        onClose={closeFindingDetails}
        title={activeModalTab === 'cve' ? 'CVE & EPSS Intelligence' : 'Vulnerability Details'}
        size={activeModalTab === 'cve' ? 'xl' : 'default'}
        footer={
          <>
            {activeModalTab === 'details' && (
              <button
                type="button"
                className="btn btn-analysis fw-semibold d-inline-flex align-items-center gap-2"
                onClick={handleFindingAnalysis}
              >
                <i className="bi bi-chat-dots-fill" />
                Analyze
              </button>
            )}
            <button
              type="button"
              className="btn btn-light fw-semibold"
              onClick={closeFindingDetails}
            >
              Close
            </button>
          </>
        }
      >
        {selectedFinding && (
          <>
            <div className="finding-modal-tabs">
              <button
                type="button"
                className={`finding-modal-tab ${activeModalTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveModalTab('details')}
              >
                Finding Details
              </button>
              <button
                type="button"
                className={`finding-modal-tab ${activeModalTab === 'cve' ? 'active' : ''}`}
                onClick={() => setActiveModalTab('cve')}
              >
                CVE &amp; EPSS ({cveCount})
              </button>
            </div>

            {activeModalTab === 'details' ? (
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            <div className="mb-3">
              <div className="small text-secondary text-uppercase fw-bold">Finding</div>
              <div className="fw-bold">{displayValue(selectedFinding.alert)}</div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-6">
                <div className="small text-secondary text-uppercase fw-bold">Severity</div>
                <div>{displayValue(selectedFinding.risk)}</div>
              </div>
              <div className="col-6">
                <div className="small text-secondary text-uppercase fw-bold">Confidence</div>
                <div>{displayValue(selectedFinding.confidence)}</div>
              </div>
            </div>
            <div className="mb-3">
              <div className="small text-secondary text-uppercase fw-bold">Affected URL</div>
              <div className="text-break">{displayValue(selectedFinding.url)}</div>
            </div>
            <div className="mb-3">
              <div className="small text-secondary text-uppercase fw-bold">Description</div>
              <div>{displayValue(selectedFinding.description)}</div>
            </div>
            <div>
              <div className="small text-secondary text-uppercase fw-bold">Recommended action</div>
              <div>{displayValue(selectedFinding.solution)}</div>
            </div>
              </div>
            ) : (
              <CveEpssTable entries={urlEpssTable} onCveClick={handleCveClick} />
            )}
          </>
        )}
      </CustomModal>
    </div>
  );
};

const BtnArea = () => {
  const { webReportNav } = useReports();
  const { currentIndex, setSelectedScanIndex } = useWeb();

  const handleReportNav = () => {
    webReportNav();
    setSelectedScanIndex(currentIndex);
  };
  
  return (
    <div className="w-100 text-center position-absolute bottom-0 start-50 translate-middle-x">
      <div
        style={{
          height: '30px',
          width: '100%',
          background: 'linear-gradient(to top, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))',
        }} />
      <div className="bg-white p-3 rounded-4">
        <Link to="/dashboard/reports">
          <button
            className="btn btn-dark border-0 px-4 py-2 rounded-3 fw-semibold report-detail-button"
            onClick={handleReportNav}>
            Show Detailed Report
          </button>
        </Link>
      </div>
    </div>
  );
};
